"use client";

import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";

type Media = {
    media?: {
        file?: string;
        alt?: string;
    };
};

type ImageItem = {
    image?: Media;
    alt?: string;
};

type Props = {
    data: {
        mainImages?: ImageItem[];
        overviewImages?: ImageItem[];
        overviewText?: string;
        mechanismImage?: Media;
        mechanismText?: string;
        specsMarkdown?: string;
        specImages?: { image?: Media; caption?: string }[];
        partsImage?: Media;
        partsText?: string;
    };
};

const TABS = [
    { key: "overview", label: "Overview" },
    { key: "mechanism", label: "Mechanism" },
    { key: "specs", label: "Specs" },
    { key: "parts", label: "Parts Information" },
];

export default function ProWhipProwhip({ data }: Props) {
    const {
        mainImages = [],
        overviewImages = [],
        overviewText,
        mechanismImage,
        mechanismText,
        specsMarkdown,
        specImages = [],
        partsImage,
        partsText,
    } = data;

    const [activeTab, setActiveTab] = useState("overview");
    const [activeHeroIndex, setActiveHeroIndex] = useState(0);

    useEffect(() => {
        const hash = window.location.hash.replace("#", "");
        if (hash && TABS.some((t) => t.key === hash)) {
            setActiveTab(hash);
        }
    }, []);

    useEffect(() => {
        window.history.replaceState(null, "", `#${activeTab}`);
    }, [activeTab]);

    const heroImage = mainImages[activeHeroIndex]?.image?.media?.file;

    return (
        <section className="w-full bg-white">

            {/* FULL WIDTH GRID */}
            <div className="w-full grid grid-cols-1 lg:grid-cols-[600px_1fr] xl:grid-cols-[720px_1fr]">

                {/* ================= LEFT IMAGE COLUMN ================= */}

                <div className="bg-gray-50 flex flex-col items-center justify-center py-12 sm:py-16 lg:py-24 px-4 sm:px-6">

                    {heroImage && (
                        <img
                            src={heroImage}
                            alt={mainImages[activeHeroIndex]?.alt || "ProWhip"}
                            className="w-full max-w-[360px] sm:max-w-[420px] lg:max-w-[520px] object-contain"
                        />
                    )}

                    {mainImages.length > 1 && (
                        <div className="flex gap-3 mt-8 flex-wrap justify-center">
                            {mainImages.map((img, i) => {
                                const thumb = img.image?.media?.file;
                                if (!thumb) return null;

                                return (
                                    <button
                                        key={i}
                                        onClick={() => setActiveHeroIndex(i)}
                                        className={`border rounded ${i === activeHeroIndex
                                                ? "border-black"
                                                : "border-gray-300"
                                            }`}
                                    >
                                        <img
                                            src={thumb}
                                            alt={img.alt || ""}
                                            className="h-12 w-16 sm:h-14 sm:w-20 object-contain"
                                        />
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* ================= RIGHT CONTENT COLUMN ================= */}

                <div className="px-6 sm:px-10 lg:px-16 py-12 sm:py-16 lg:py-24">

                    {/* Tabs */}

                    <div className="flex gap-6 sm:gap-10 border-b mb-8 sm:mb-12 overflow-x-auto">

                        {TABS.map((tab) => (
                            <button
                                key={tab.key}
                                onClick={() => setActiveTab(tab.key)}
                                className={`pb-3 text-sm tracking-wide whitespace-nowrap ${activeTab === tab.key
                                        ? "border-b-2 border-black font-semibold"
                                        : "text-gray-400"
                                    }`}
                            >
                                {tab.label}
                            </button>
                        ))}

                    </div>

                    {/* ================= TAB CONTENT ================= */}

                    {activeTab === "overview" && (
                        <>
                            {overviewText && (
                                <div className="prose max-w-none mb-10 sm:mb-12">
                                    <ReactMarkdown>{overviewText}</ReactMarkdown>
                                </div>
                            )}

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 sm:gap-x-16 gap-y-10 sm:gap-y-16">

                                {overviewImages.map((item, i) => {
                                    const src = item.image?.media?.file;
                                    if (!src) return null;

                                    return (
                                        <img
                                            key={i}
                                            src={src}
                                            alt={item.alt || ""}
                                            className="w-full max-w-[260px] sm:max-w-[320px] lg:max-w-[360px]"
                                        />
                                    );
                                })}

                            </div>
                        </>
                    )}

                    {activeTab === "mechanism" && (
                        <>
                            {mechanismText && (
                                <div className="prose max-w-none mb-10 sm:mb-12">
                                    <ReactMarkdown>{mechanismText}</ReactMarkdown>
                                </div>
                            )}

                            {mechanismImage?.media?.file && (
                                <img
                                    src={mechanismImage.media.file}
                                    alt=""
                                    className="w-full max-w-[400px] sm:max-w-[500px] lg:max-w-[600px]"
                                />
                            )}
                        </>
                    )}

                    {activeTab === "specs" && (
                        <>
                            {/* Top Images */}

                            <div className="flex flex-wrap gap-6 sm:gap-12 mb-12 sm:mb-16">

                                {specImages.map((s, i) => {
                                    const src = s.image?.media?.file;
                                    if (!src) return null;

                                    return (
                                        <img
                                            key={i}
                                            src={src}
                                            alt={s.caption || ""}
                                            className="w-[160px] sm:w-[220px] lg:w-[280px] object-contain"
                                        />
                                    );
                                })}

                            </div>

                            {/* Specs Table */}

                            {specsMarkdown && (
                                <div className="max-w-[600px]">

                                    <ReactMarkdown
                                        components={{
                                            p: ({ children }) => (
                                                <div className="flex justify-between border-b py-3 text-sm">
                                                    {children}
                                                </div>
                                            ),
                                            strong: ({ children }) => (
                                                <span className="font-semibold text-gray-700">
                                                    {children}
                                                </span>
                                            ),
                                        }}
                                    >
                                        {specsMarkdown}
                                    </ReactMarkdown>

                                </div>
                            )}
                        </>
                    )}

                    {activeTab === "parts" && (
                        <>
                            <div className="bg-black text-white text-center py-3 mb-10 text-sm font-medium">
                                Parts Information
                            </div>

                            {partsImage?.media?.file && (
                                <div className="flex justify-center">
                                    <img
                                        src={partsImage.media.file}
                                        alt="Parts Information"
                                        className="w-full max-w-[600px] lg:max-w-[900px] object-contain"
                                    />
                                </div>
                            )}
                        </>
                    )}

                </div>
            </div>
        </section>
    );
}
